<template>
  <!-- parameter value editing fields -->
  <div>
    <q-input
      class="q-pa-none"
      v-if="[0, 1, 2].includes(parameterValueType)"
      v-model="theModel.stringValue"
      outlined
      dense
      autogrow
      multiline
      no-error-icon
      autofocus
      debounce="500"
      :rules="
        parameterValueType === 1
          ? intRules
          : parameterValueType === 2
          ? floatRules
          : []
      "
    />

    <!-- use input if open type and not a date -->
    <q-select
      v-else-if="parameterValueType === 4"
      :label="vsetTitle || $t('values_set')"
      ref="valueset_field"
      outlined
      dense
      v-model="valueSetModel"
      :use-input="[1, 2].includes(vsetType || 0) && parameterSetValueType !== 3"
      autofocus
      option-label="Name"
      option-value="Id"
      @new-value="addTextParameterValue"
      :options="vsetFilteredOptions"
      input-debounce="100"
      @popup-hide="
        () => {
          searchText = '';
        }
      "
      popup-content-class="my-sticky-select-menu"
    >
      <template v-slot:prepend>
        <q-btn flat v-if="parameterSetValueType === 3" icon="event">
          <q-menu fit>
            <q-date
              :locale="dateLocale"
              no-unset
              minimal
              v-model="theModel.dateValueSetModel"
              mask="YYYY-MM-DDTHH:mm:ssZ"
            />
          </q-menu>
        </q-btn>
      </template>

      <!-- Слот перед списком опций -->
      <template v-slot:before-options>
        <div class="search-sticky-container q-pa-xs bg-white">
          <q-input
            outlined
            dense
            ref="vset_search_input"
            color="white"
            class="thin-input q-my-auto"
            v-model="searchText"
            hide-bottom-space
            clearable
            :clear-icon="clearIconName"
            :placeholder="$t('search_label')"
            debounce="300"
            @clear="searchText = ''"
          >
            <template v-slot:append>
              <q-icon name="search" color="accent" />
            </template>
          </q-input>
        </div>
      </template>
      <template v-slot:no-option>
        <!-- Повторяем поле поиска здесь, иначе оно исчезнет вместе с before-options -->
        <div class="q-pa-xs bg-white">
          <q-input
            outlined
            dense
            ref="vset_search_input"
            color="white"
            class="thin-input q-my-auto"
            v-model="searchText"
            hide-bottom-space
            clearable
            :clear-icon="clearIconName"
            :placeholder="$t('search_label')"
            debounce="300"
            @clear="searchText = ''"
          >
            <template v-slot:append>
              <q-icon name="search" color="accent" />
            </template>
          </q-input>
          <div class="text-grey q-pa-md text-center">{{ $t('nothing_found') }}</div>
        </div>
      </template>
    </q-select>

    <div class="row no-wrap" v-else-if="parameterValueType === 6">
      <q-btn flat icon="event">
        <q-menu fit>
          <q-date
            :locale="dateLocale"
            no-unset
            minimal
            v-model="theModel.dateValueModel"
            mask="YYYY-MM-DDTHH:mm:ssZ"
          />
        </q-menu>
      </q-btn>
      <!-- display date value model -->
      <div class="q-pa-sm">
        {{ displayDateFormat(theModel.dateValueModel) }}
      </div>
    </div>

    <mosta-checkbox
      v-else-if="parameterValueType === 3"
      v-model="theModel.checkBoxModel"
      :label="$t('yes_label') + ' / ' + $t('no_label')"
    />
  </div>
</template>

<script lang="ts">
import { date } from "quasar";
import { Component, Emit, Prop, Vue, Watch } from "vue-facing-decorator";
import { calendarLocale } from "@/utils/auxfunc";
import { i18n } from "../../i18n";
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';

export interface ParamsRow {
  stringValue: string;
  dateValueModel: string;
  checkBoxModel: boolean;
  // value set models
  vsetValues: any;
  dateValueSetModel: string;
}

@Component({
  name: "MultiEditLine",
  components: { MostaCheckbox },
})
export default class MultiEditLine extends Vue {
  private clearIconUrl: string = require("@/assets/arrow_back.svg");
  // вычисляемое свойство, которое Quasar ожидает в виде строки "img:URL"
  get clearIconName(): string {
    return `img:${this.clearIconUrl}`;
  }

  // v-model
  @Prop({ default: {} as ParamsRow })
  modelValue: any | undefined;
  @Emit("update:modelValue") emitModelValue(arg: ParamsRow | undefined) {
    return arg;
  }

  // model data
  @Prop({ default: [] })
  vsetOptions!: any[];
  @Prop({ default: 1 })
  parameterValueType!: number;
  @Prop({ default: 1 })
  vsetType!: number;
  @Prop({ default: 1 })
  parameterSetValueType!: number;
  @Prop({ default: "" })
  vsetTitle?: string;

  dateLocale = calendarLocale;
  searchText = "";

  // rules
  intRules = [
    (val: any) =>
      !val || !this.isNumeric(val) || i18n.global.t("enter_integer"),
  ];
  floatRules = [
    (val: any) =>
      !val || !this.isNumericFloat(val) || i18n.global.t("input_float"),
  ];

  isNumeric(str: string) {
    // if (typeof str != "string") return false // we only process strings!
    return (
      !isNaN(+str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
      !isNaN(parseInt(str))
    ); // ...and ensure strings of whitespace fail
  }

  isNumericFloat(str: string) {
    // if (typeof str != "string") return false // we only process strings!
    return (
      !isNaN(+str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
      !isNaN(parseFloat(str))
    ); // ...and ensure strings of whitespace fail
  }

  displayDateFormat(val: string): string {
    return val
      ? date.formatDate(new Date("" + val), "DD.MM.YYYY")
      : this.$t("not_set_condition");
  }

  // common linear chart model
  get theModel(): ParamsRow {
    return this.modelValue;
  }

  set theModel(val: ParamsRow | undefined) {
    this.emitModelValue(val);
  }

  // value set model
  get valueSetModel(): any[] {
    return this.theModel.vsetValues || [];
  }

  set valueSetModel(val: any[]) {
    this.theModel.vsetValues = val;
  }

  get vsetFilteredOptions() {
    if (this.searchText) {
      // Фильтруем список на основе ввода в поле поиска
      const needle = this.searchText.toLowerCase();
      return this.vsetOptions.filter(
        (v) => v.Name.toLowerCase().indexOf(needle) > -1
      );
    } else {
      return this.vsetOptions;
    }
  }

  @Watch("theModel.dateValueSetModel")
  addDateVSetValue(arg: string) {
    if (arg) {
      const found_el = this.vsetOptions.find((el) => el.Name.trim() === arg);
      if (found_el) {
        // @ts-ignore
        this.$refs.valueset_field.add(found_el, true);
      } else {
        // @ts-ignore
        this.$refs.valueset_field.add(
          {
            Id: "0_" + arg,
            Name: date.formatDate(new Date(Date.parse("" + arg)), "DD.MM.YYYY"),
            NameDate: arg,
            new: true,
          },
          true
        );
      }

      this.$nextTick(() => {
        this.theModel.dateValueSetModel = "";
      });
    }
  }

  addTextParameterValue(val: string, done: any) {
    // TODO: show validation error if not correct
    const found_el = this.vsetOptions.find((el) => el.Name.trim() === val);
    if (found_el) {
      done(found_el, "add-unique");
    } else {
      if (this.parameterSetValueType === 0) {
        done(
          {
            Id: "0_" + val,
            Name: val,
            NameString: val,
            new: true,
          },
          "add-unique"
        );
      } else {
        if (
          (this.parameterSetValueType === 1 && this.isNumeric(val)) ||
          (this.parameterSetValueType === 2 && this.isNumericFloat(val))
        ) {
          // Number
          done(
            {
              Id: "0_" + val,
              Name: +parseFloat(val),
              NameNumber: +parseFloat(val),
              new: true,
            },
            "add-unique"
          );
        }
      }
    }
  }
}
</script>

<style lang="scss">
.minw-selector {
  font-size: small !important;
  min-width: fit-content;
}
</style>
